'use client';

import * as React from 'react';
import Image from 'next/image';
import { ArrowUpRight, Play } from 'lucide-react';

import { routes } from '@workspace/routes';
import { Button } from '@workspace/ui/components/customButton';

import featureHeroBg from '~/public/assets/hero/feature-hero-bg.png';
import heroDecoClapper from '~/public/assets/hero/hero-deco-clapper.png';
import heroDecoPlay from '~/public/assets/hero/hero-deco-play.png';
import { HeroProps } from '~/types/hero-type';
import { BlurFade } from '../fragments/blur-fade';
import { SiteHeading } from '../fragments/site-heading';

import { trackCta } from '~/lib/track-cta';

// Default Vimeo video configuration (shared product demo)
const HERO_VIDEO = {
  embedUrl:
    'https://player.vimeo.com/video/1159090878?autoplay=1&title=0&byline=0&portrait=0',
  thumbnail: 'https://gw-ai-social-media.b-cdn.net/VideoMule/thumb8.png',
  alt: 'VideoMule product demo video'
};

export default function HeroSection({
  badge,
  title,
  description,
  ctaText,
  ctaHref,
  id,
  isFeatureHero = true,
  video
}: HeroProps): React.JSX.Element {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const heroVideo = video ?? HERO_VIDEO;

  return (
    <section
      className={isFeatureHero ? 'relative' : 'bg-gradient-header relative'}
      id={id}
      style={
        isFeatureHero
          ? {
              backgroundImage: `url(${featureHeroBg.src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }
          : undefined
      }
    >
      <div className="container">
        <SiteHeading
          badge={badge}
          title={title}
          description={description}
        />

        <BlurFade
          inView
          delay={0.3}
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10 lg:mb-20 w-fit mx-auto -mt-3">
            <Button
              href={ctaHref || routes.dashboard.auth.SignUp}
              onClick={() => trackCta('cta_signup_clicked', { surface: 'tool_hero', path: window.location.pathname })}
              size="lg"
              className="group text-base lg:text-lg px-8 py-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex items-center justify-center"
            >
              {ctaText}
              <span className="flex items-center justify-center size-6 lg:size-8 rounded-full bg-videomule-white transition-transform duration-300 group-hover:translate-x-1">
                <ArrowUpRight className="text-videomule-black size-4 lg:size-5" />
              </span>
            </Button>
          </div>
        </BlurFade>

        {/* Browser Mockup with Dashboard */}
        <BlurFade
          inView
          delay={0.4}
          className="relative max-w-7xl mx-auto"
        >
          <div className="relative w-full aspect-video">
            {/* Decorative Play Icon - Top Left */}
            <div className="absolute -top-8 -left-12 sm:-top-10 sm:-left-16 z-10 animate-float hidden sm:block">
              <Image
                src={heroDecoPlay}
                alt="Video play icon symbolizing tutorial or media playback"
                className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24"
              />
            </div>

            {/* Decorative Clapperboard Icon - Bottom Right */}
            <div className="absolute -bottom-8 -right-8 sm:-right-12 z-10 animate-float hidden sm:block">
              <Image
                src={heroDecoClapper}
                alt="Clapperboard icon symbolizing video creation and editing"
                className="w-14 h-14 sm:w-18 sm:h-18 lg:w-22 lg:h-22"
              />
            </div>
            {isPlaying ? (
              <div className="w-full h-full rounded-2xl overflow-hidden bg-black">
                <iframe
                  src={heroVideo.embedUrl}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  className="w-full h-full"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsPlaying(true)}
                className="relative w-full h-full group cursor-pointer"
              >
                <Image
                  src={heroVideo.thumbnail}
                  alt={heroVideo.alt}
                  fill
                  priority
                  sizes="(max-width: 1280px) 100vw, 1280px"
                  className="rounded-lg object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="size-10 lg:size-20 bg-videomule-green rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play
                      className="size-4 lg:size-8 text-white"
                      fill="white"
                    />
                  </div>
                </div>
              </button>
            )}
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent pointer-events-none rounded-2xl"></div>
        </BlurFade>

        {/* Section separator */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      </div>
    </section>
  );
}
